import BarChart from 'ui/baseComponents/Charts/BarChart'
import { Stack } from 'ui/baseComponents/Stack'
import { Text } from 'ui/baseComponents/Text'
import ReportChartWithTitle from '../../components/ReportChartWithTitle'
import { colors } from 'theme/colors'
import { summedArray } from '../../helpers'
import { PERIODS } from 'ui/clinicianScreens/Patient/Report/consts'
import ReportMetricTrendIcon from '../../components/ReportMetricTrendIcon'
import { LabelList } from 'recharts'
import {
  calculateWeeklyActivityDurations,
  countWeeksAtOrOverTarget,
  getAveragePerWeek,
} from 'ui/clinicianScreens/Patient/Report/sections/Activity/helpers'
import { useReportTranslation } from 'utilities/i18n'

export const ReportChartActivityGoal = ({
  currentData,
  startTime,
  prevData,
  prevStartTime,
}) => {
  const { t } = useReportTranslation()
  const STYLES = {
    chartsContainer: {
      paddingTop: '10px',
      paddingBottom: '20px',
      '& .recharts-reference-line .recharts-label': {
        transform: 'translate(-200px, 0)',
        fill: colors.LIME[700],
        fontWeight: 700,
      },
      '& .recharts-label-list:not(:last-child) .recharts-label': {
        fill: colors.BLACK,
        fontWeight: 700,
      },
      '& .recharts-xAxis .recharts-label': {
        fill: colors.BLACK,
      },
    },
    chartContainer: {
      position: 'relative',
      flex: 1,
    },
    averagePerWeekText: {
      position: 'absolute',
      top: -12,
      left: 25,
    },
  }

  const currentWeeklyActivityDurations = calculateWeeklyActivityDurations(
    currentData,
    startTime,
  )
  const prevWeeklyActivityDurations = calculateWeeklyActivityDurations(
    prevData,
    prevStartTime,
  )

  const findHighestActivityDuration = (currentDurations, prevDurations) => {
    const allDurations = [...currentDurations, ...prevDurations]
    return Math.max(...allDurations)
  }

  const yAxisMax = findHighestActivityDuration(
    currentWeeklyActivityDurations,
    prevWeeklyActivityDurations,
  )

  const weeksAtOrOverTargetCurrent = countWeeksAtOrOverTarget(
    currentWeeklyActivityDurations,
  )

  const weeksAtOrOverTargetPrev = countWeeksAtOrOverTarget(
    prevWeeklyActivityDurations,
  )

  const currentTotalDurationInMinutes = summedArray(
    currentWeeklyActivityDurations,
  )

  const prevTotalDurationInMinutes = summedArray(prevWeeklyActivityDurations)

  const currentAveragePerWeek = getAveragePerWeek(currentTotalDurationInMinutes)

  const prevAveragePerWeek = `${Math.floor(prevTotalDurationInMinutes / 4)}m`

  const noData = !currentData.length

  const hasTrendChange = weeksAtOrOverTargetCurrent !== weeksAtOrOverTargetPrev
  const trend = weeksAtOrOverTargetCurrent > weeksAtOrOverTargetPrev
  const hasActivityDuration = currentTotalDurationInMinutes > 0

  const Trend = () => (
    <Stack direction="row">
      {hasActivityDuration ? (
        <>
          <Text variant="body16B">
            {t(
              weeksAtOrOverTargetCurrent === 1
                ? 'report.activity.goalAchievedWeekOne'
                : 'report.activity.goalAchievedWeekOther',
              { count: weeksAtOrOverTargetCurrent },
            )}
          </Text>
          {hasTrendChange && (
            <ReportMetricTrendIcon
              direction={trend ? 'up' : 'down'}
              variant={trend ? 'better' : 'worse'}
            />
          )}
          <Text variant="body16">
            {t(
              weeksAtOrOverTargetPrev === 1
                ? 'report.activity.goalPreviousWeekOne'
                : 'report.activity.goalPreviousWeekOther',
              { count: weeksAtOrOverTargetPrev },
            )}
          </Text>
        </>
      ) : (
        <Text variant="body16">{t('report.common.noActivityTime')}</Text>
      )}
    </Stack>
  )

  const GoalChart = ({ legendItem, dataValue, weeklyAverage, chartId }) => {
    // Keep the series identity stable and English so localized legend text
    // doesn't reshape the chart data.
    const seriesKey = legendItem.dataKey

    return (
      <div style={STYLES.chartContainer}>
        <BarChart
          {...{
            height: 200,
            legend: true,
            legendItem,
            barSize: 50,
            showBarLabels: true,
            xAxisLabel: t('report.chart.xAxisMinutesPerWeek'),
            xAxisInterval: 1,
            yAxisInterval: 100,
            yAxisMax: yAxisMax < 150 ? 150 : yAxisMax,
            hideGridLines: true,
            hideYAxis: true,
            isAnimationActive: false,
            data: [
              {
                [seriesKey]: dataValue[0],
                weekLabel: 'w1',
              },
              {
                [seriesKey]: dataValue[1],
                weekLabel: 'w2',
              },
              {
                [seriesKey]: dataValue[2],
                weekLabel: 'w3',
              },
              {
                [seriesKey]: dataValue[3],
                weekLabel: 'w4',
              },
            ],
            referenceLineValue: 150,
            referenceLineLabel: '150m',
            secondaryBarLabels: (
              <LabelList dataKey="weekLabel" position="bottom" offset={25} />
            ),
            margin: { top: 25, right: 16, left: 60, bottom: 40 },
            barRadius: [6, 6, 0, 0],
            chartId: `activity-goal-chart-${chartId}`,
          }}
        />
        <Text variant="body16" sx={STYLES.averagePerWeekText}>
          {t('report.activity.goalAvgWeekLabel')}
          <Text variant="body16B">{weeklyAverage}</Text>
        </Text>
      </div>
    )
  }

  return (
    <ReportChartWithTitle
      {...{
        noData,
        title: t('report.activity.goalTitle'),
        subtitle: hasActivityDuration && t('report.activity.goalSubtitle'),
        trend: <Trend />,
      }}
    >
      {hasActivityDuration && (
        <>
          <Stack
            direction="row"
            spacing={5}
            alignItems="center"
            sx={STYLES.chartsContainer}
          >
            <GoalChart
              legendItem={{
                dataKey: PERIODS.recent,
                name: t('report.chart.periodRecent'),
                color: colors.PRIMARY[1000],
              }}
              dataValue={currentWeeklyActivityDurations}
              weeklyAverage={currentAveragePerWeek}
              chartId="recent"
            />
            <GoalChart
              legendItem={{
                dataKey: PERIODS.previous,
                name: t('report.chart.periodPrevious'),
                color: colors.PRIMARY[700],
              }}
              dataValue={prevWeeklyActivityDurations}
              weeklyAverage={prevAveragePerWeek}
              chartId="previous"
            />
          </Stack>
        </>
      )}
    </ReportChartWithTitle>
  )
}
